import { FC, ReactNode } from 'react';
import { Box, Divider, LinearProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

export interface ReviewStats {
  extracted: number;
  total: number;
  needsAttention: number;
  empty: number;
  youEdited: number;
  rejected: number;
}

interface Props {
  stats: ReviewStats;
}

const Stat: FC<{ label: ReactNode; value: ReactNode; color?: string; extra?: ReactNode }> =
  observer(({ label, value, color, extra }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 64 }}>
      <Typography variant="textXS_semiBold" color="text.tertiary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography variant="textLG_bold" color={color ?? 'text.primary'}>
          {value}
        </Typography>
        {extra}
      </Box>
    </Box>
  ));

/** The horizontal extraction summary bar at the top of the review screen. */
export const ReviewStatStrip: FC<Props> = observer(({ stats }) => {
  const { t } = useTrans();
  const pct = stats.total > 0 ? (stats.extracted / stats.total) * 100 : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, md: 4 },
        flexWrap: 'wrap',
        p: 2,
        borderRadius: 2,
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'grey.300',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 160, flex: '1 1 160px' }}>
        <Typography variant="textXS_semiBold" color="text.tertiary">
          {t('app.documentHub.review.stat.extracted')}
        </Typography>
        <Typography variant="textLG_bold" color="text.primary">
          {stats.extracted}
          <Box component="span" sx={{ typography: 'textSM_regular', color: 'text.disabled' }}>
            /{stats.total}
          </Box>
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': { backgroundColor: 'text.primary' },
          }}
        />
      </Box>

      <Divider orientation="vertical" flexItem sx={{ borderColor: 'grey.200' }} />

      <Stat
        label={t('app.documentHub.review.stat.needsAttention')}
        value={stats.needsAttention}
        color="yellow.800"
      />
      <Divider orientation="vertical" flexItem sx={{ borderColor: 'grey.200' }} />
      <Stat label={t('app.documentHub.review.stat.youEdited')} value={stats.youEdited} color="blue.700" />
      <Divider orientation="vertical" flexItem sx={{ borderColor: 'grey.200' }} />
      <Stat
        label={t('app.documentHub.review.stat.rejected')}
        value={stats.rejected}
        color="error.main"
      />
    </Box>
  );
});
